import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { AnimatePresence, motion, useSpring, useTransform } from 'framer-motion';
import { navLinks } from '../data/siteContent';

const linkClasses = ({ isActive }) =>
  `uppercase tracking-wide text-sm font-semibold transition-all duration-300 relative group ${
    isActive
      ? 'text-tertiary'
      : 'text-slate-700 hover:text-tertiary'
  } after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-gradient-to-r after:from-primary after:to-secondary after:transition-all after:duration-300 group-hover:after:w-full`;

const barVariants = {
  closed: () => ({ rotate: 0, y: 0, opacity: 1 }),
  open: (index) => {
    if (index === 1) {
      return { opacity: 0 };
    }
    if (index === 0) {
      return { rotate: 45, y: 6 };
    }
    return { rotate: -45, y: -6 };
  },
};

const MotionNavLink = motion(NavLink);

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [isCondensed, setIsCondensed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsCondensed(window.scrollY > 80);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const smoothProgress = useSpring(0, { stiffness: 190, damping: 26, mass: 0.8 });

  useEffect(() => {
    smoothProgress.set(isCondensed ? 1 : 0);
  }, [isCondensed, smoothProgress]);

  const headerBackground = useTransform(smoothProgress, (value) => `rgba(255, 255, 255, ${0.45 + value * 0.25})`);
  // Primary color RGB values (#0D409D = rgb(13, 64, 157))
  const primaryRGB = '13, 64, 157';

  const headerShadow = useTransform(
    smoothProgress,
    (value) => `0 16px 32px -16px rgba(${primaryRGB}, ${0.04 + value * 0.08})`
  );
  const wrapperPaddingX = useTransform(smoothProgress, (value) => `${16 - value * 4}px`);
  const wrapperPaddingY = useTransform(smoothProgress, (value) => `${12 - value * 4}px`);
  const panelPaddingY = useTransform(smoothProgress, (value) => `${16 - value * 6}px`);
  const panelPaddingX = useTransform(smoothProgress, (value) => `${16 + value * 4}px`);
  const panelRadius = useTransform(smoothProgress, (value) => `${12 + value * 24}px`);
  const panelMarginTop = useTransform(smoothProgress, (value) => `${value * 6}px`);
  const panelBackground = useTransform(smoothProgress, (value) => `rgba(255, 255, 255, ${0.4 + value * 0.35})`);
  const panelBorderColor = useTransform(smoothProgress, (value) => `rgba(${primaryRGB}, ${0.1 + value * 0.15})`);
  const panelShadow = useTransform(
    smoothProgress,
    (value) => `0 16px 32px -20px rgba(${primaryRGB}, ${0.06 + value * 0.1})`
  );
  const navGap = useTransform(smoothProgress, (value) => `${28 - value * 6}px`);
  const menuButtonBackground = useTransform(smoothProgress, (value) => `rgba(${primaryRGB}, ${0.06 + value * 0.08})`);

  const toggleMenu = () => setIsOpen((prev) => !prev);
  const closeMenu = () => setIsOpen(false);

  return (
    <motion.header
      className="fixed left-0 right-0 top-0 z-50"
      style={{
        backgroundColor: headerBackground,
        boxShadow: headerShadow,
        backdropFilter: 'blur(18px) saturate(180%)',
      }}
    >
      <motion.div
        className="mx-auto w-full max-w-6xl"
        style={{
          paddingLeft: wrapperPaddingX,
          paddingRight: wrapperPaddingX,
          paddingTop: wrapperPaddingY,
          paddingBottom: wrapperPaddingY,
        }}
      >
        <motion.div
          className="flex w-full items-center justify-between gap-6 border backdrop-blur-2xl bg-gradient-to-r from-white/40 to-white/30"
          style={{
            borderRadius: panelRadius,
            paddingLeft: panelPaddingX,
            paddingRight: panelPaddingX,
            paddingTop: panelPaddingY,
            paddingBottom: panelPaddingY,
            marginTop: panelMarginTop,
            backgroundColor: panelBackground,
            borderColor: panelBorderColor,
            boxShadow: panelShadow,
          }}
        >
          <MotionNavLink
            to="/"
            className="flex items-center hover:opacity-80 transition-opacity duration-300"
            onClick={closeMenu}
          >
            <img
              src="/logo-dark.png"
              alt="Duoley Logo"
              className="h-10 w-auto"
              style={{ height: isCondensed ? '32px' : '40px' }}
            />
          </MotionNavLink>
          <motion.nav className="hidden items-center md:flex" style={{ columnGap: navGap }}>
            {navLinks.map(({ label, path }) => (
              <NavLink key={path} to={path} className={linkClasses} onClick={closeMenu}>
                {label}
              </NavLink>
            ))}
          </motion.nav>
          <motion.button
            type="button"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-primary/40 text-primary md:hidden hover:border-primary/80 transition-all duration-300 hover:shadow-lg hover:shadow-primary/20"
            style={{ backgroundColor: menuButtonBackground, backdropFilter: 'blur(14px)' }}
            aria-label="Toggle navigation"
            onClick={toggleMenu}
          >
            <span className="sr-only">Toggle menu</span>
            <div className="relative flex h-5 w-6 flex-col justify-between">
              {[0, 1, 2].map((index) => (
                <motion.span
                  key={index}
                  className="block h-0.5 w-full rounded-full bg-primary"
                  custom={index}
                  variants={barVariants}
                  animate={isOpen ? 'open' : 'closed'}
                  transition={{ duration: 0.3 }}
                />
              ))}
            </div>
          </motion.button>
        </motion.div>
      </motion.div>
      <AnimatePresence>
        {isOpen && (
          <motion.nav
            className="md:hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="glass-panel mx-4 mt-3 flex flex-col gap-3 rounded-3xl px-6 py-5 text-center">
              {navLinks.map(({ label, path }) => (
                <NavLink key={path} to={path} className={linkClasses} onClick={closeMenu}>
                  {label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
